import { format } from "path";
import { stringify } from "querystring";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import { NumericBox } from "../components/common/NumericBox";
import { SelectBox } from "../components/common/SelectBox";
import { TextArea } from "../components/common/TextArea";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import PopupContent from "../components/popupContent";
import MultipleProductGroupSelector from "../components/products/MultipleProductGroupSelector";
import { FormatString } from "../helpers/stringHelpers";
import TranslationContext from "../providers/TranslationContext";
import { ValidationProvider } from "../providers/ValidationContext";
import { addNotification } from "../redux/actions";
import { apiGetAllProductGroups, apiGetProductGroups } from "../services/ProductsService";
import {
  apiGetAllSellers,
  apiGetSellerDetail,
  apiSellerCreateOrUpdate,
} from "../services/SellersService";
import { validateForm } from "../validation/formValidation";
import { model } from "../validation/models/seller";
import PopupOperationsBar from "./PopupOperationsBar";

// styles
require("./PopupGeneralStyles.css");

interface Props {
  id?: number;
  handleCancel: any;
  refreshData: any;
}

const SellersPopup = (props: Props) => {
  const translationContext = useContext(TranslationContext) as any;
  const prefix = translationContext.prefix;
  const { t } = useTranslation(undefined, { keyPrefix: `${prefix}.Popup` });
  const cloneDeep = require('clone-deep');
  const [validationModel, setValidationModel] = useState(cloneDeep(model));
  const [loading, setLoading] = useState(true);
  const data: any = {};
  const [formData, setFormData] = useState(data);
  const [saveError, setSaveError] = useState(false);
  const [productGroupsError, setProductGroupsError] = useState(false);

  const [productGroups, setProductGroups] = useState([]);
  const [selectedProductGroups, setSelectedProductsGroups] = useState<any[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userCountry, setUserCountry] = useState(null);

  const isEditMode = props.id ? true : false;

  const dispatch = useDispatch();

  const userInfo = useSelector((state: any) => state.userInfo);

  useEffect(() => {
    if (userInfo) {
      setIsSuperAdmin(userInfo.IsSuperAdmin);
      setUserCountry(userInfo.CountryCode);
      if (!userInfo.IsSuperAdmin) {
        formData['countryCode'] = userInfo.CountryCode;
        setFormData(formData);
      }
    }
  }, [userInfo])

  useEffect(() => {
    if (props.id) {
      apiGetSellerDetail(props.id).then((resp) => {
        if (resp.status === 200) {
          setFormData(resp.data);
          setSelectedProductsGroups(resp.data.productGroups);
        }
        else {
          props.handleCancel();
          dispatch(addNotification({ text: FormatString(t('LoadingDetailsError'), `${props.id}`), type: 'error' }));
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
    apiGetAllProductGroups()
      .then((resp: any) => {
        setProductGroups(resp.data);
      })
      .catch((ex: any) => {

      });
  }, []);

  const onAddingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });

    if (!formData.productGroups || formData.productGroups.length === 0) {
      setProductGroupsError(true);
      return;
    }

    if (validationModel.isValid) {
      setLoading(true);
      apiSellerCreateOrUpdate(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('CreateSuccess'), formData.name), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('CreateFailure'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onEditingClick = () => {
    validateForm(validationModel, formData, isEditMode);
    setValidationModel({ ...validationModel });

    if (!formData.productGroups || formData.productGroups.length === 0) {
      setProductGroupsError(true);
      return;
    }

    if (validationModel.isValid) {
      setLoading(true);
      apiSellerCreateOrUpdate(formData)
        .then((responseData) => {
          dispatch(addNotification({ text: FormatString(t('EditSuccess'), formData.name), type: 'success' }));
          props.refreshData();
          props.handleCancel();

          setLoading(false);
        })
        .catch((err: any) => {
          dispatch(addNotification({ text: FormatString(t('EditFailure'), formData.name), type: 'error' }));
          setSaveError(true);
        });
    }
  };

  const onCancelClick = () => {
    props.handleCancel();
  };

  const handleProductGroupsChange = (groups: any) => {
    setSelectedProductsGroups([...groups]);

    formData.productGroups = groups;
    setFormData(formData);

    if (groups && groups.length > 0 && productGroupsError) {
      setProductGroupsError(false);
    }
  }

  return (
    <PopupContent header={props.id ? t('EditLabel') : t('CreateLabel')}>
      <ValidationProvider value={validationModel}>
        <>
          <TextBox
            dataField="name"
            defaultValue={formData.name}
            formData={formData}
            setStateMethod={setFormData}
            id="name"
            label={t('Labels.name')}
            placeholder={t('Labels.namePlaceholder')}
          />
          <TextBox
            dataField="identificationNumber"
            defaultValue={formData.identificationNumber}
            formData={formData}
            setStateMethod={setFormData}
            id="identificationNumber"
            label={t('Labels.identificationNumber')}
            placeholder={t('Labels.identificationNumberPlaceholder')}
          />
          <NumericBox
            dataField="vat"
            defaultValue={formData.vat}
            formData={formData}
            setStateMethod={setFormData}
            id="vat"
            label={t('Labels.vat')}
          />
          <TextBox
            dataField="email"
            defaultValue={formData.email}
            formData={formData}
            setStateMethod={setFormData}
            id="email"
            label={t('Labels.email')}
            placeholder={t('Labels.emailPlaceholder')}
          />
          <SelectBox
            dataField="currency"
            value={formData.currency}
            formData={formData}
            label={t('Labels.currency')}
            options={[{ value: "CZK", label: "CZK" }, { value: "EUR", label: "EUR" }, { value: "PLN", label: "PLN" }, { value: "BGN", label: "BGN" }]}
            placeholder={t('Labels.currencyPlaceholder')}
            setStateMethod={setFormData}
            isDisabled={false}
          />
          <TextBox
            dataField="city"
            defaultValue={formData.city}
            formData={formData}
            setStateMethod={setFormData}
            id="city"
            label={t('Labels.city')}
            placeholder={t('Labels.cityPlaceholder')}
          />
          <TextBox
            dataField="zipCode"
            defaultValue={formData.zipCode}
            formData={formData}
            setStateMethod={setFormData}
            id="zipCode"
            label={t('Labels.zipCode')}
            placeholder={t('Labels.zipCodePlaceholder')}
          />
          <TextBox
            dataField="mobile"
            defaultValue={formData.mobile}
            formData={formData}
            setStateMethod={setFormData}
            id="mobile"
            label={t('Labels.mobile')}
            placeholder={t('Labels.mobilePlaceholder')}
          />
          <TextBox
            dataField="phone"
            defaultValue={formData.phone}
            formData={formData}
            setStateMethod={setFormData}
            id="phone"
            label={t('Labels.phone')}
            placeholder={t('Labels.phonePlaceholder')}
          />
          <TextBox
            dataField="street"
            defaultValue={formData.street}
            formData={formData}
            setStateMethod={setFormData}
            id="street"
            label={t('Labels.street')}
            placeholder={t('Labels.streetPlaceholder')}
          />
          {isSuperAdmin && <SelectBox
            dataField="countryCode"
            value={formData.countryCode}
            formData={formData}
            label={t('Labels.countryCode')}
            options={[{ value: "CZ", label: "CZ" }, { value: "PL", label: "PL" }, { value: "SK", label: "SK" }, { value: "BG", label: "BG" }, { value: "BR", label: "BR" }, { value: "HR", label: "HR" }, { value: "KZ", label: "KZ" }, { value: "UA", label: "UA" }]}
            placeholder={t('Labels.countryCodePlaceholder')}
            setStateMethod={setFormData}
            isDisabled={false}
          />}
          {!isSuperAdmin && <TextBox
            dataField="countryCode"
            defaultValue={formData.countryCode}
            formData={formData}
            setStateMethod={setFormData}
            id="countryCode"
            label={t('Labels.countryCode')}
            placeholder={t('Labels.countryCodePlaceholder')}
            disabled={true}
          />}

          <div style={{ backgroundColor: productGroupsError ? '#FFCCCB' : 'unset' }}>
            <MultipleProductGroupSelector

              productGroups={productGroups}
              selectedGroups={selectedProductGroups}
              onChange={handleProductGroupsChange}
            />
          </div>
          <PopupOperationsBar
            handleAdding={onAddingClick}
            handleCancellation={onCancelClick}
            handleEditing={onEditingClick}
            id={props.id}
          />
        </>
      </ValidationProvider>
    </PopupContent>
  );
};

export default SellersPopup;
